
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { verifyOtp } from "@/utils/supabase/auth";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { 
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

interface OtpVerificationProps {
  email: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const OtpVerification = ({ email, onSuccess, onCancel }: OtpVerificationProps) => {
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const navigate = useNavigate();

  const handleVerify = async () => {
    if (!otp || otp.length < 6) {
      toast({
        title: "Error",
        description: "Please enter a valid verification code",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    
    try {
      const { data, error } = await verifyOtp(email, otp);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Verification successful",
      });
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/entries");
      }
    } catch (error) {
      console.error("Verification error:", error);
      toast({
        title: "Verification failed",
        description: error instanceof Error ? error.message : "Could not verify code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2 text-center">
        <h3 className="text-lg font-medium">Verification required</h3>
        <p className="text-sm text-muted-foreground">
          Please enter the 6-digit code sent to<br />
          <span className="font-medium">{email}</span>
        </p>
      </div>
      
      <div className="flex justify-center py-4">
        <InputOTP
          maxLength={6}
          value={otp}
          onChange={setOtp}
          render={({ slots }) => (
            <InputOTPGroup>
              {slots.map((slot, index) => (
                <InputOTPSlot key={index} {...slot} index={index} />
              ))}
            </InputOTPGroup>
          )}
        />
      </div>

      <div className="flex justify-between gap-2">
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={onCancel}
          disabled={isVerifying}
        >
          Cancel
        </Button>
        <Button 
          className="w-full" 
          onClick={handleVerify}
          disabled={isVerifying || otp.length < 6}
        >
          {isVerifying ? "Verifying..." : "Verify"}
        </Button>
      </div>
    </div>
  );
};

export default OtpVerification;
